import { ref } from 'vue';
import { getCart } from '@/api/cart';

const TOKEN_KEY = 'cart_token';
const COUNT_KEY  = 'cart_count';
const CONFIRMED_KEY = 'cart_confirmed';

function genToken(): number {
  const arr = new Uint32Array(1);
  crypto.getRandomValues(arr);
  return (arr[0] % 900000000) + 100000000;
}

function readLS(key: string, fallback: number): number {
  try { const v = localStorage.getItem(key); return v ? Number(v) : fallback; } catch { return fallback; }
}
function writeLS(key: string, val: number) {
  try { localStorage.setItem(key, String(val)); } catch {}
}

const _token = ref<number>(0);
const _count = ref<number>(0);
// CLI only ever sends `token` in the add-to-cart payload once a cart genuinely exists on the
// server (item.token = getSessionData("cartToken") || null — null gets stripped by
// changeNullToEmpty on the very first add). Sending our own client-generated token on that
// first call — before the server has ever created a cart under it — appears to be exactly
// what was causing add-to-cart to fail: the server was never asked to create a cart, just to
// add an item to one that (from its point of view) doesn't exist.
const _confirmed = ref<boolean>(false);
let _ready = false;

function ensureReady() {
  if (_ready) return;
  _ready = true;
  const stored = readLS(TOKEN_KEY, 0);
  _token.value = stored || genToken();
  _count.value = readLS(COUNT_KEY, 0);
  _confirmed.value = readLS(CONFIRMED_KEY, 0) === 1;
  // Only persist the token if the user is authenticated — prevents writing cart_token
  // to localStorage during Ionic transition animations that fire after logout.
  if (!stored && localStorage.getItem('auth_token')) writeLS(TOKEN_KEY, _token.value);
}

export function resetCartSession() {
  _ready = false;
  _token.value = 0;
  _count.value = 0;
  _confirmed.value = false;
  try {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(COUNT_KEY);
    localStorage.removeItem(CONFIRMED_KEY);
  } catch {}
}

export function useCartSession() {
  ensureReady();
  function setCount(n: number) { _count.value = n; writeLS(COUNT_KEY, n); }
  function clearCart() {
    const t = genToken(); _token.value = t; _count.value = 0; _confirmed.value = false;
    writeLS(TOKEN_KEY, t); writeLS(COUNT_KEY, 0); writeLS(CONFIRMED_KEY, 0);
  }
  // Re-syncs to the server's own cart token (CLI: setSessionData("cartToken", ...) after
  // every add-to-cart) — the client-generated token is only provisional until the server
  // actually creates a cart under it; if it hands back a different one, that's the real one.
  function setToken(t: number) {
    _confirmed.value = true;
    writeLS(CONFIRMED_KEY, 1);
    if (!t || t === _token.value) return;
    _token.value = t;
    writeLS(TOKEN_KEY, t);
  }
  // Re-checks the persisted badge count against the server's real cart, instead of trusting
  // whatever was last written to localStorage — called on every page view (see DefaultLayout.vue)
  // so the header badge can never get stuck showing a stale/phantom number for long, no matter
  // which screen the user is on when the underlying cart changes or an add-to-cart is interrupted
  // before it's confirmed.
  async function refreshFromServer() {
    if (!_confirmed.value && _count.value === 0) return;
    const cart = await getCart(_token.value);
    if (!cart) {
      _confirmed.value = false; writeLS(CONFIRMED_KEY, 0);
      setCount(0);
      return;
    }
    setCount(cart.cart_items?.length ?? 0);
  }
  return { cartToken: _token, itemCount: _count, hasServerCart: _confirmed, setCount, clearCart, setToken, refreshFromServer };
}
